import { gaPushEvent } from "../base.analytics";
import type { CongressPhase } from "../../../../../api/src/shared/domain/congress-lifecycle";

enum Event {
  COUNTDOWN_SHOWN = "congress_countdown_shown",
  COUNTDOWN_TAPPED = "congress_countdown_tapped",
  SPACE_VIEWED = "congress_space_viewed",
  SESSIONS_CARD_VIEWED = "congress_sessions_card_viewed",
  SESSIONS_OPENED = "congress_sessions_opened",
  SESSION_LINK_CLICKED = "congress_session_link_clicked",
  PDF_CARD_OPENED = "congress_pdf_card_opened",
}

/**
 * Identity every congress event carries (tracking plan §V.2). Built once where
 * the space payload is read and drilled down, so no call site has to re-derive
 * the phase or guess which of a room's congresses it is reporting on.
 */
export type CongressAnalyticsContext = {
  spaceId: string;
  congressName: string;
  phase: CongressPhase;
  roomId: string;
};

/** Which of the three doors into a congress space the user came through. */
export type CongressEntryPoint =
  "banner" | "room_block" | "chip" | "resources_tab" | "deep_link";

/** The two surfaces a countdown card is drawn on. */
export type CongressCountdownSurface = "discovery_banner" | "room_hero";

/**
 * The §V.2 identity as flat event props. Exported because content opens
 * attributed to a congress space (`playlist_opened`, `content_viewed`) reuse it
 * verbatim: the family must spell `congress_space_id` the same way everywhere,
 * or filters and breakdowns stop lining up across the board's tiles.
 */
export function congressContextProps(context: CongressAnalyticsContext) {
  return {
    congress_space_id: context.spaceId,
    congress_name: context.congressName,
    phase: context.phase,
    room_id: context.roomId,
  };
}

export function gaEventCongressCountdownShown(payload: {
  context: CongressAnalyticsContext;
  surface: CongressCountdownSurface;
}) {
  gaPushEvent(Event.COUNTDOWN_SHOWN, {
    ...congressContextProps(payload.context),
    surface: payload.surface,
  });
}

export function gaEventCongressCountdownTapped(payload: {
  context: CongressAnalyticsContext;
  surface: CongressCountdownSurface;
}) {
  gaPushEvent(Event.COUNTDOWN_TAPPED, {
    ...congressContextProps(payload.context),
    surface: payload.surface,
  });
}

export function gaEventCongressSpaceViewed(payload: {
  context: CongressAnalyticsContext;
  entryPoint: CongressEntryPoint;
}) {
  gaPushEvent(Event.SPACE_VIEWED, {
    ...congressContextProps(payload.context),
    entry_point: payload.entryPoint,
  });
}

export function gaEventCongressSessionsCardViewed(payload: {
  context: CongressAnalyticsContext;
  sessionsCount: number;
}) {
  gaPushEvent(Event.SESSIONS_CARD_VIEWED, {
    ...congressContextProps(payload.context),
    sessions_count: payload.sessionsCount,
  });
}

export function gaEventCongressSessionsOpened(payload: {
  context: CongressAnalyticsContext;
  sessionsCount: number;
}) {
  gaPushEvent(Event.SESSIONS_OPENED, {
    ...congressContextProps(payload.context),
    sessions_count: payload.sessionsCount,
  });
}

export function gaEventCongressSessionLinkClicked(payload: {
  context: CongressAnalyticsContext;
  sessionId: string;
  /** 0-based rank in the curated list */
  position: number;
}) {
  gaPushEvent(Event.SESSION_LINK_CLICKED, {
    ...congressContextProps(payload.context),
    session_id: payload.sessionId,
    position: payload.position,
  });
}

export function gaEventCongressPdfCardOpened(payload: {
  context: CongressAnalyticsContext;
  cardId: string;
  /** Admin-authored label ("Brochure", "Floorplan", …) — free text, not an enum */
  cardLabel: string;
  /** 0-based rank in the space's content order */
  position: number;
}) {
  gaPushEvent(Event.PDF_CARD_OPENED, {
    ...congressContextProps(payload.context),
    card_id: payload.cardId,
    card_label: payload.cardLabel,
    position: payload.position,
  });
}
